/**
 * Convierte un string tipo "3-3-4", "4-2-4" o "1-3-3-4" (GK explícito) en
 * un array de 10 números = jugadores de campo por línea, desde la portería propia hacia el centro.
 * Suma debe ser 10 (o 11 con primer segmento 1 = portero, que se omite para el layout).
 */
export function parseFormationString(str) {
  if (str == null || typeof str !== 'string') return null;
  let parts = str
    .trim()
    .split(/[\s-]+/)
    .map((p) => parseInt(p, 10));
  if (!parts.length || parts.some((n) => !Number.isFinite(n) || n < 1)) return null;
  let sum = parts.reduce((a, b) => a + b, 0);
  if (sum === 11 && parts[0] === 1) {
    parts = parts.slice(1);
    sum = parts.reduce((a, b) => a + b, 0);
  }
  if (sum !== 10) return null;
  return parts;
}

export function normalizeFormationOrDefault(str, fallback) {
  return parseFormationString(str) || fallback;
}

/**
 * Invierte el orden de los segmentos (ataque→defensa ↔ defensa→ataque).
 * Reservado para llamadas externas; el layout de cancha usa el mismo criterio local/visitante
 * (primer número = línea más cercana a la portería propia del equipo).
 */
export function invertFormationStringForAway(str) {
  const parts = parseFormationString(str);
  if (!parts || !parts.length) return str;
  return parts.slice().reverse().join('-');
}

/** Debe coincidir con `.wca-lineaciones__pitch` height y `.wca-lineaciones__pitch-avatar` height en styles.module.scss */
const PITCH_HEIGHT_PX = 296;
const PITCH_AVATAR_HEIGHT_PX = 30;

/**
 * `top` en % (centro del marcador del jugador, con translate -50% -50%).
 * Columnas de 5: 22px entre bordes verticales de avatares; de 4: 40px; de 3: 57px.
 * De 2: reparto proporcional como antes.
 */
function verticalTopsForRow(count) {
  if (count <= 0) return [];
  if (count === 1) return [50];
  if (count === 3 || count === 4 || count === 5) {
    const gapBetweenAvatarsPx =
      count === 4 ? 40 : count === 3 ? 57 : 22;
    const centerDistPx = PITCH_AVATAR_HEIGHT_PX + gapBetweenAvatarsPx;
    const midY = PITCH_HEIGHT_PX / 2;
    const span = (count - 1) * centerDistPx;
    const firstCenterY = midY - span / 2;
    return Array.from({ length: count }, (_, j) => {
      const yPx = firstCenterY + j * centerDistPx;
      return (yPx / PITCH_HEIGHT_PX) * 100;
    });
  }
  const lo = 14;
  const hi = 86;
  return Array.from({ length: count }, (_, j) => lo + ((j + 0.5) / count) * (hi - lo));
}

/**
 * Posiciones en % dentro de la cancha desktop (600×296).
 * Índice 0 = portero; 1..10 = líneas según `lines` (primera cifra = línea más cercana a la portería propia).
 */
export function buildDesktopPitchPositions(lines, isHome) {
  const rowCount = lines.length;
  const out = [];
  out.push({ left: isHome ? 8 : 92, top: 50 });

  const leftForLine = (lineIndex) => {
    if (isHome) {
      const lo = 18;
      const hi = 42;
      if (rowCount <= 1) return (lo + hi) / 2;
      return lo + (lineIndex / (rowCount - 1)) * (hi - lo);
    }
    const lo = 58;
    const hi = 82;
    if (rowCount <= 1) return (lo + hi) / 2;
    return hi - (lineIndex / (rowCount - 1)) * (hi - lo);
  };

  lines.forEach((n, lineIndex) => {
    const left = leftForLine(lineIndex);
    const tops = verticalTopsForRow(n);
    for (let j = 0; j < n; j += 1) {
      out.push({ left, top: tops[j] });
    }
  });
  return out;
}

export function applyFormationToPitchPlayers(players, formationStr, isHome, fallbackLines) {
  const lines = normalizeFormationOrDefault(formationStr, fallbackLines);
  const positions = buildDesktopPitchPositions(lines, isHome);
  if (!players || players.length !== positions.length) {
    return (players || []).map((p) => ({ ...p, isHome: p.isHome != null ? p.isHome : isHome }));
  }
  return players.map((p, i) => ({
    ...p,
    left: positions[i].left,
    top: positions[i].top,
    isHome: p.isHome != null ? p.isHome : isHome,
  }));
}

/**
 * Filas de arriba a abajo en móvil (vista: arriba = bajo palos, abajo = hacia el mediocampo/semicírculo).
 * Misma convención que desktop: `formationStr` = portería propia → ataque (p. ej. 4-2-3-1 = 4+2+3+1).
 */
export function buildMobileVerticalRows(players, formationStr, fallbackLines) {
  const F = normalizeFormationOrDefault(formationStr, fallbackLines);
  if (!players || players.length !== 11) {
    return [players || []];
  }
  const expected = 1 + F.reduce((a, b) => a + b, 0);
  if (expected !== 11) {
    return [players || []];
  }
  const lineSlices = [];
  let start = 1;
  for (let i = 0; i < F.length; i += 1) {
    lineSlices.push(players.slice(start, start + F[i]));
    start += F[i];
  }
  if (start !== 11) {
    return [players || []];
  }
  return [[players[0]], ...lineSlices];
}
